import type { CSSProperties, ReactNode } from 'react';

import { cn } from '@/shared/lib/utils';
import { FieldLabel } from '@/shared/ui/form-field/FieldLabel';
import { InlineFieldLabel } from '@/shared/ui/form-field/InlineFieldLabel';

export type FieldLayout = 'stacked' | 'inline';

export interface FieldProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  layout?: FieldLayout;
  labelWidth?: number;
  htmlFor?: string;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}

/**
 * Phoenix form field wrapper.  Provides a label, optional helper/error message
 * and a required-indicator dot, with stacked (default) and inline layouts.
 * The wrapped control inherits the required state via the `[data-required]`
 * attribute placed on the parent — Phoenix inputs key off this to apply the
 * warm yellow background fill.
 */
export function Field({
  label,
  hint,
  error,
  required,
  layout = 'stacked',
  labelWidth = 132,
  htmlFor,
  children,
  className,
  style,
}: FieldProps) {
  return (
    <div
      data-layout={layout}
      data-required={required ? '' : undefined}
      className={cn(
        'phx-field flex min-w-0',
        layout === 'inline' ? 'gap-phx-4 flex-row items-start' : 'gap-phx-2 flex-col',
        className
      )}
      style={style}
    >
      {label ? (
        layout === 'inline' ? (
          <InlineFieldLabel htmlFor={htmlFor} required={required} width={labelWidth}>
            {label}
          </InlineFieldLabel>
        ) : (
          <FieldLabel htmlFor={htmlFor} required={required}>
            {label}
          </FieldLabel>
        )
      ) : null}
      <div className="phx-field-control gap-phx-1_5 flex min-w-0 flex-1 flex-col">
        {children}
        {error || hint ? (
          <div
            data-error={error ? '' : undefined}
            className={cn(
              'phx-field-msg text-phx-xs',
              error ? 'text-phx-danger' : 'text-phx-text-muted'
            )}
          >
            {error ?? hint}
          </div>
        ) : null}
      </div>
    </div>
  );
}
